import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Menu, X, Phone, Mail, Globe, Music2, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";

function useMemoSettings(settings) {
  return React.useMemo(() => ({ settings }), [settings]);
}

function Logo({ size = "md" }) {
  const big = size === "lg";
  return (
    <Link to="/" data-testid="brand-logo" className="inline-flex items-center gap-3 group">
      <div className={`${big ? "w-12 h-12" : "w-10 h-10"} border border-gold flex items-center justify-center rounded-sm bg-black group-hover:shadow-gold transition-all`}>
        <Music2 className="text-gold" size={big ? 22 : 18} />
      </div>
      <div className="leading-none">
        <div className={`font-heading ${big ? "text-3xl" : "text-2xl"} font-bold`}>
          <span className="text-white">Song</span>
          <span className="text-gold">niture</span>
        </div>
        {big && <div className="text-[10px] tracking-[0.3em] text-gold/80 uppercase mt-1">We Turn Brands Into Songs That Resonate</div>}
      </div>
    </Link>
  );
}

const navItems = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/samples", label: "Samples" },
  { to: "/pricing", label: "Pricing" },
  { to: "/process", label: "Process" },
  { to: "/contact", label: "Contact" },
];

function Header({ settings }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-black/85 backdrop-blur-md border-b border-ink-700">
      <div className="container-x flex items-center justify-between py-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.2em] uppercase font-semibold transition-colors ${
                  isActive ? "text-gold" : "text-white/80 hover:text-gold"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {user && user.role ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="nav-dashboard"
                className="btn-outline"
              >
                {user.role === "admin" ? "Admin" : "Dashboard"}
              </Link>
              <button onClick={logout} data-testid="nav-logout" className="text-white/70 hover:text-gold text-sm uppercase tracking-[0.2em]">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-white/80 hover:text-gold text-[12px] tracking-[0.2em] uppercase font-semibold">
                Login
              </Link>
              <Link to="/contact" data-testid="nav-lets-talk" className="btn-gold">
                Let&apos;s Talk
              </Link>
            </>
          )}
        </div>
        <button
          className="lg:hidden text-gold p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-ink-700 bg-black">
          <div className="container-x py-6 flex flex-col gap-4">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mnav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-[0.18em] uppercase font-semibold ${
                    isActive ? "text-gold" : "text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="h-px bg-ink-700 my-2" />
            {user && user.role ? (
              <>
                <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="btn-outline w-fit">
                  {user.role === "admin" ? "Admin" : "Dashboard"}
                </Link>
                <button onClick={logout} className="text-white/70 text-left">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-white">Login</Link>
                <Link to="/contact" className="btn-gold w-fit">Let&apos;s Talk</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer({ settings }) {
  return (
    <footer className="bg-ink-900 border-t border-ink-700 mt-24">
      <div className="container-x py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo size="lg" />
          <p className="mt-6 text-white/60 max-w-md">
            Songniture creates custom theme songs, jingles, and brand soundtracks that turn companies into
            unforgettable musical experiences.
          </p>
          <div className="mt-6 flex items-center gap-3 text-white/40">
            <a href="#" className="hover:text-gold" data-testid="social-facebook"><Facebook size={18} /></a>
            <a href="#" className="hover:text-gold" data-testid="social-instagram"><Instagram size={18} /></a>
            <a href="#" className="hover:text-gold" data-testid="social-linkedin"><Linkedin size={18} /></a>
            <a href="#" className="hover:text-gold" data-testid="social-youtube"><Youtube size={18} /></a>
          </div>
        </div>
        <div>
          <div className="eyebrow mb-4">Explore</div>
          <ul className="space-y-3 text-white/70 text-sm">
            {navItems.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-gold">{n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">Contact</div>
          <ul className="space-y-3 text-white/70 text-sm">
            <li className="flex items-center gap-2"><Phone size={14} className="text-gold" /> {settings?.phone}</li>
            <li className="flex items-center gap-2"><Mail size={14} className="text-gold" /> {settings?.email}</li>
            <li className="flex items-center gap-2"><Globe size={14} className="text-gold" /> {settings?.website}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ink-700">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} {settings?.business_name || "Songniture"}. All Rights Reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-gold">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  const outletContext = useMemoSettings(settings);
  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Header settings={settings} />
      <main className="flex-1">
        <Outlet context={outletContext} />
      </main>
      <Footer settings={settings} />
    </div>
  );
}
